import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { UI } from '@/Components/UI';
import { useApi, useDebounce } from '@/hooks';
import { Food } from '@/types';
import { FilterDialog } from './FilterDialog';
import { useTranslation } from 'react-i18next';

type FoodFilterDialogProps = {
    value: string[] | undefined;
    onChange: (food: string[]) => void;
}

type FoodFilterContentProps = Omit<FoodFilterDialogProps, 'onChange'>;

type FoodFilterContentRef = {
    getSelectedFood: () => string[];
}

export const FoodFilter = ({ value, onChange }: FoodFilterDialogProps) => {
    const { t } = useTranslation(['search']);

    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef<FoodFilterContentRef>(undefined);

    function apply() {
        const selectedFood = contentRef.current?.getSelectedFood();
        onChange(selectedFood ?? []);
        setIsOpen(false);
    }

    return (
        <>
            <UI.Button
                buttonStyle={(value?.length ?? 0) > 0 ? 'tonal' : 'outline'}
                className={'py-1'}
                onClick={() => setIsOpen(true)}>
                <UI.Icon name={'egg_alt'} />&nbsp;{t('filters.food.title', { count: value?.length ?? 0 })}
            </UI.Button>

            <FilterDialog
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onApply={apply}>
                <FoodFilterDialogContent
                    ref={contentRef}
                    value={value} />
            </FilterDialog>
        </>
    );
};

const FoodFilterDialogContent = forwardRef(
    ({ value }: FoodFilterContentProps, ref: Ref<FoodFilterContentRef | undefined>) => {
        const { t } = useTranslation('misc');

        const { SearchService } = useApi();
        const [search, setSearch] = useState('');
        const throttledSearch = useDebounce(search, 500);

        const [selectedFood, setSelectedFood] = useState<Food[]>([]);
        const [searchResult, setSearchResult] = useState<Food[]>([]);

        // Initial load of selected food
        useEffect(() => {
            // No food selected
            if (value === undefined || value.length === 0) {
                return;
            }

            SearchService
                .listFood(value)
                .then((response) => setSelectedFood(response.data))
                .catch(console.error);
        }, []);

        // Load new search results
        useEffect(() => {
            SearchService
                .searchFood(throttledSearch)
                .then((response) => setSearchResult(response.data))
                .catch(console.error);
        }, [throttledSearch]);

        useImperativeHandle(
            ref,
            () => ({
                getSelectedFood: () =>
                    selectedFood.map((food) => food.id).filter((id) => id !== undefined)
            }),
            [selectedFood]
        );

        const onSelected = (food: Food) => {
            if (selectedFood.some((item) => item.id === food.id)) {
                // Remove the food from the selected list
                setSelectedFood(selectedFood.filter((item) => item.id !== food.id));
            } else {
                // Add the food to the selected list
                setSelectedFood([...selectedFood, food]);
            }
        };

        return (
            <>
                <UI.Form.SearchInput
                    value={search}
                    onChange={setSearch} />

                <div className={'flex flex-row gap-2 overflow-x-scroll'}>
                    {selectedFood.length === 0 && (
                        <UI.Button
                            buttonStyle={'text'}
                            disabled>
                            {t('noneSelected')}
                        </UI.Button>
                    )}

                    {selectedFood.map((item) => (
                        <UI.Button
                            key={item.id}
                            className={'flex flex-row items-center gap-2 whitespace-nowrap'}
                            onClick={() => onSelected(item)}>
                            <UI.Icon name={'close'} />

                            <span>
                                {item.name}
                            </span>
                        </UI.Button>
                    ))}
                </div>

                <div className={'flex flex-col gap-1 mt-2'}>
                    {searchResult.map((item) => (
                        <UI.Button
                            key={item.id}
                            buttonStyle={selectedFood.some((food) => food.id === item.id) ? 'filled' : 'tonal'}
                            onClick={() => onSelected(item)}
                            className={'w-full rounded-none first:rounded-t-xl last:rounded-b-xl'}>
                            {item.name}
                        </UI.Button>
                    ))}
                </div>
            </>
        );
    });
